import React, { useState, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store/useAppStore';
import { useAuth } from '@/hooks/useAuth';
import nexusLogo from '@/assets/nexus-logo.png';
import { useTheme } from 'next-themes';
import {
  LayoutDashboard, Megaphone, Users, Workflow, MessageSquare, Bot,
  BarChart3, Plug, Settings, Sparkles, ChevronLeft, ChevronRight, ChevronDown,
  LogOut, User, CreditCard, ShoppingBag, Paintbrush, Package, ClipboardList, FileText, Users2,
  Handshake, Activity, Ticket, Search, TrendingUp, Share2, Crown, Layers, ShoppingCart, Download,
  Moon, Sun
} from 'lucide-react';

type NavItem = { path: string; label: string; icon: any };
type NavSubgroup = { id: string; label: string; icon?: any; items: NavItem[] };
type NavSection = { id: string; label: string | null; icon?: any; items?: NavItem[]; subgroups?: NavSubgroup[] };

const navSections: NavSection[] = [
  {
    id: 'overview',
    label: null,
    items: [
      { path: '/app', label: 'Tableau de bord', icon: LayoutDashboard },
    ],
  },
  {
    id: 'boutique',
    label: 'Boutique',
    icon: ShoppingBag,
    subgroups: [
      {
        id: 'boutique-catalogue',
        label: 'Catalogue',
        icon: Package,
        items: [
          { path: '/app/boutique/produits', label: 'Produits', icon: Package },
          { path: '/app/boutique/categories', label: 'Catégories', icon: Layers },
          { path: '/app/boutique/coupons', label: 'Coupons', icon: Ticket },
          { path: '/app/boutique/flash-sales', label: 'Flash Sales', icon: Sparkles },
        ],
      },
      {
        id: 'boutique-ventes',
        label: 'Ventes',
        icon: ClipboardList,
        items: [
          { path: '/app/boutique/commandes', label: 'Commandes', icon: ClipboardList },
          { path: '/app/boutique/clients', label: 'Clients', icon: Users2 },
          { path: '/app/boutique/abandoned-carts', label: 'Paniers abandonnés', icon: ShoppingCart },
          { path: '/app/boutique/reviews', label: 'Avis clients', icon: Activity },
        ],
      },
      {
        id: 'boutique-marketing',
        label: 'Marketing',
        icon: Megaphone,
        items: [
          { path: '/app/boutique/segmentation', label: 'Segmentation', icon: Layers },
          { path: '/app/boutique/loyalty', label: 'Fidélité', icon: Crown },
          { path: '/app/boutique/referrals', label: 'Parrainage', icon: Share2 },
        ],
      },
      {
        id: 'boutique-perso',
        label: 'Personnalisation',
        icon: Paintbrush,
        items: [
          { path: '/app/boutique/design', label: 'Design', icon: Paintbrush },
          { path: '/app/boutique/pages', label: 'Pages & Menu', icon: FileText },
          { path: '/app/boutique/seo', label: 'SEO', icon: Search },
        ],
      },
      {
        id: 'boutique-config',
        label: 'Configuration',
        icon: Settings,
        items: [
          { path: '/app/boutique/config', label: 'Configuration', icon: Settings },
        ],
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    subgroups: [
      {
        id: 'marketing-campagnes',
        label: 'Campagnes & Contacts',
        icon: Megaphone,
        items: [
          { path: '/app/campagnes', label: 'Campagnes', icon: Megaphone },
          { path: '/app/contacts', label: 'Contacts', icon: Users },
          { path: '/app/emails-sms', label: 'Emails & SMS', icon: MessageSquare },
        ],
      },
      {
        id: 'marketing-automation',
        label: 'Automatisation',
        icon: Workflow,
        items: [
          { path: '/app/automatisations', label: 'Automatisations', icon: Workflow },
        ],
      },
      {
        id: 'marketing-analyse',
        label: 'Publicité & Analyse',
        icon: BarChart3,
        items: [
          { path: '/app/ads', label: 'Ads (Meta & TikTok)', icon: Megaphone },
        ],
      },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: Handshake,
    subgroups: [
      {
        id: 'crm-main',
        label: 'Pipeline',
        icon: Handshake,
        items: [
          { path: '/app/crm', label: 'Pipeline', icon: Handshake },
          { path: '/app/crm/activites', label: 'Activités', icon: Activity },
        ],
      },
    ],
  },
  {
    id: 'system',
    label: 'Système',
    icon: Settings,
    subgroups: [
      {
        id: 'system-integrations',
        label: 'Intégrations & IA',
        icon: Plug,
        items: [
          { path: '/app/integrations', label: 'Intégrations', icon: Plug },
          { path: '/app/agents', label: 'IA & Agents (MCP)', icon: Bot },
        ],
      },
      {
        id: 'system-config',
        label: 'Configuration',
        icon: Settings,
        items: [
          { path: '/app/parametres', label: 'Paramètres', icon: Settings },
          { path: '/app/installer', label: 'Installer l\'app', icon: Download },
        ],
      },
    ],
  },
];

const HIDDEN_SECTION_IDS = new Set(['marketing', 'crm']);

export const AppSidebar: React.FC = () => {
  const { sidebarOpen, toggleSidebar } = useAppStore();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [systemMenuOpen, setSystemMenuOpen] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const darkMode = resolvedTheme === 'dark';

  const isItemActive = (path: string) => {
    const isExactOnly = path === '/app' || path === '/app/boutique' || path === '/app/crm';
    return isExactOnly
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const sectionAllItems = (s: NavSection): NavItem[] =>
    s.items ?? (s.subgroups?.flatMap((g) => g.items) ?? []);

  const activeSectionId = useMemo(
    () => navSections.find((s) => sectionAllItems(s).some((i) => isItemActive(i.path)))?.id ?? null,
    [location.pathname]
  );




  const tabbedSections = navSections.filter(
    (s) => !!s.label && s.id !== 'system' && !HIDDEN_SECTION_IDS.has(s.id)
  );
  const overviewSection = navSections.find((s) => !s.label);
  const systemSection = navSections.find((s) => s.id === 'system');
  const systemItems: NavItem[] = systemSection?.subgroups?.flatMap((g) => g.items) ?? [];

  const [activeTab, setActiveTab] = useState<string>(
    activeSectionId && activeSectionId !== 'overview' && activeSectionId !== 'system'
      ? activeSectionId
      : tabbedSections[0]?.id
  );
  React.useEffect(() => {
    if (activeSectionId && activeSectionId !== 'overview' && activeSectionId !== 'system') setActiveTab(activeSectionId);
  }, [activeSectionId]);

  const currentSection = tabbedSections.find((s) => s.id === activeTab) ?? tabbedSections[0];

  const displayName = user?.user_metadata?.prenom || user?.email?.split('@')[0] || 'Utilisateur';
  const displayLastName = user?.user_metadata?.nom || '';
  const displayEmail = user?.email || '';

  const renderItem = (item: NavItem) => {
    const isActive = isItemActive(item.path);
    return (
      <NavLink
        key={item.path}
        to={item.path}
        title={!sidebarOpen ? item.label : undefined}
        className={cn(
          'relative flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200',
          isActive
            ? 'bg-primary/15 text-primary'
            : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
        )}
      >
        <item.icon className={cn('w-4 h-4 flex-shrink-0', isActive && 'text-primary')} />
        <AnimatePresence>
          {sidebarOpen && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="whitespace-nowrap"
            >
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
        {isActive && (
          <motion.div
            layoutId="activeNav"
            className="absolute left-0 w-1 h-6 rounded-r-full bg-primary"
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Desktop sidebar */}
      <motion.aside
        className={cn(
          'hidden md:flex flex-col fixed left-0 top-0 bottom-0 z-40 glass border-r border-border',
        )}
        animate={{ width: sidebarOpen ? 260 : 72 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-border">
          <motion.img
            src={nexusLogo}
            alt="Nexus Market"
            width={36}
            height={36}
            className="w-9 h-9 flex-shrink-0"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
          />
          <AnimatePresence>
            {sidebarOpen && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="font-display font-bold text-lg text-foreground whitespace-nowrap"
              >
                Nexus <span className="text-primary">Market</span>
              </motion.span>
            )}
          </AnimatePresence>
          {sidebarOpen && overviewSection?.items?.[0] && (() => {
            const item = overviewSection.items[0];
            const active = isItemActive(item.path);
            return (
              <NavLink
                to={item.path}
                title={item.label}
                className={cn(
                  'ml-auto p-2 rounded-lg transition-colors flex-shrink-0',
                  active
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
                )}
              >
                <item.icon className="w-4 h-4" />
              </NavLink>
            );
          })()}
        </div>

        {/* Overview (Tableau de bord) - shown only when collapsed */}
        {!sidebarOpen && overviewSection && (
          <div className="px-2 pt-3">
            {overviewSection.items?.map((item) => renderItem(item))}
          </div>
        )}

        {/* Tabs */}
        <div className={cn('px-2 pt-3', sidebarOpen ? 'grid gap-1' : 'flex flex-col gap-1')}
          style={sidebarOpen ? { gridTemplateColumns: `repeat(${tabbedSections.length}, minmax(0, 1fr))` } : undefined}
        >
          {tabbedSections.map((s) => {
            const isActive = s.id === activeTab;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setActiveTab(s.id)}
                title={s.label ?? ''}
                className={cn(
                  'flex items-center justify-center gap-1 rounded-lg text-[11px] font-medium transition-all min-w-0',
                  sidebarOpen ? 'px-1.5 py-2' : 'w-full py-2',
                  isActive
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
                )}
              >
                {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                {sidebarOpen && <span className="truncate">{s.label}</span>}
              </button>
            );
          })}
        </div>

        {/* Navigation for active tab (flat, grouped by subsection labels) */}
        <nav className="flex-1 py-3 px-2 space-y-3 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSection?.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              className="space-y-3"
            >
              {currentSection?.items && (
                <div className="space-y-1">
                  {currentSection.items.map((item) => renderItem(item))}
                </div>
              )}
              {currentSection?.subgroups?.map((sg) => (
                <div key={sg.id} className="space-y-1">
                  {sidebarOpen ? (
                    <div className="flex items-center gap-2 px-2 pt-2 pb-1.5">
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-foreground/70">
                        {sg.label}
                      </span>
                      <span className="h-px flex-1 bg-border" aria-hidden="true" />
                    </div>
                  ) : (
                    <div className="mx-auto w-8 border-t border-border my-1" />
                  )}
                  {sg.items.map((item) => renderItem(item))}
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </nav>


        {/* User & collapse */}
        <div className="border-t border-border p-3 space-y-2">
          <button
            type="button"
            onClick={() => setTheme(darkMode ? 'light' : 'dark')}
            title={darkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
            aria-label={darkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
          >
            {darkMode ? <Sun className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
            {sidebarOpen && <span className="text-sm font-medium">{darkMode ? 'Mode clair' : 'Mode sombre'}</span>}
          </button>
          <div className="relative">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex-1 flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-foreground/5 transition-colors min-w-0"
            >
              <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
                {displayName[0]}{displayLastName?.[0] || ''}
              </div>
              {sidebarOpen && (
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{displayName} {displayLastName}</p>
                  <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>
                </div>
              )}
            </button>
            {sidebarOpen && systemItems.length > 0 && (
              <button
                onClick={() => setSystemMenuOpen(!systemMenuOpen)}
                title="Système"
                className={cn(
                  'p-2 rounded-lg transition-colors flex-shrink-0 border border-border bg-background/40',
                  activeSectionId === 'system' || systemMenuOpen
                    ? 'text-primary bg-primary/20 border-primary/40'
                    : 'text-foreground hover:text-primary hover:bg-foreground/10'
                )}
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
          </div>

          <AnimatePresence>
            {userMenuOpen && sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute bottom-full left-0 right-0 mb-2 glass rounded-xl border border-border p-1 z-50"
              >
                <button className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-foreground/5 rounded-lg">
                  <User className="w-4 h-4" /> Mon profil
                </button>
                <button className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-foreground/5 rounded-lg">
                  <CreditCard className="w-4 h-4" /> Facturation
                </button>
                <hr className="border-border my-1" />
                <button onClick={async () => { await signOut(); navigate('/auth'); }} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg">
                  <LogOut className="w-4 h-4" /> Déconnexion
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {systemMenuOpen && sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute bottom-full left-0 right-0 mb-2 bg-popover/95 backdrop-blur-xl rounded-xl border border-border shadow-2xl p-1 z-50"
              >
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  Système
                </div>
                {systemItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSystemMenuOpen(false)}
                    className={({ isActive }) => cn(
                      'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                      isActive ? 'bg-primary/15 text-primary' : 'text-foreground hover:bg-foreground/5'
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center py-1.5 rounded-lg hover:bg-foreground/5 text-muted-foreground transition-colors"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </motion.aside>

      {/* Mobile bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-border flex justify-around py-2 px-1">
        {[
          { path: '/app', label: 'Accueil', icon: LayoutDashboard },
          { path: '/app/boutique/produits', label: 'Produits', icon: Package },
          { path: '/app/boutique/commandes', label: 'Commandes', icon: ClipboardList },
          { path: '/app/campagnes', label: 'Campagnes', icon: Megaphone },
          { path: '/app/contacts', label: 'Contacts', icon: Users },
        ].map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={cn(
                'flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[10px]',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="truncate max-w-[60px]">{item.label.split(' ')[0]}</span>
            </NavLink>
          );
        })}
      </nav>
      <button
        type="button"
        onClick={() => setTheme(darkMode ? 'light' : 'dark')}
        aria-label={darkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
        className="md:hidden fixed right-3 top-3 z-50 w-10 h-10 rounded-xl glass flex items-center justify-center text-foreground"
      >
        {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    </>
  );
};
